import React from 'react';
import { AlertTriangle, Lock } from 'lucide-react';
import { useTenantZone } from '../hooks/useTenantTimeZone';
import { useAuth } from '../contexts/AuthContext';

const REPAIR_PATH = '/settings/regional-tax';

/**
 * Nothing under this renders until the tenant's zone is known.
 *
 * Every authenticated route tree sits below it (App.tsx), which is what lets a
 * screen treat `useTenantTimeZone()` as answered: a date on any screen is
 * computed in `companies.time_zone` or not computed at all. The alternative —
 * each of ~150 date sites carrying its own loading and error branch — is how a
 * site gets missed and quietly falls back to the browser's zone.
 *
 * The wait is one request, made alongside the screen's own, and after the
 * first load on a device it is not a wait at all offline: the device copy
 * (lib/tenant-zone.ts) answers when the call cannot.
 *
 * NOT a retry button and not an illustration — one line saying what happened,
 * per the load-failure convention. Sign Out is offered because this screen
 * replaces the whole app, so there is no other way off it.
 */
export function TenantZoneGate({ children }: { children: React.ReactNode }) {
  const zone = useTenantZone();
  const { logout } = useAuth();

  if (zone.status === 'ready' || zone.status === 'signed-out') return <>{children}</>;
  // The one page that can repair a missing zone has to stay reachable, or the
  // message below sends an admin to a screen this gate is hiding. That page
  // shows no business dates. (The server would answer "missing" only for a
  // row written around the API's allowlist; the column defaults to a zone.)
  if (zone.status === 'error' && zone.reason === 'missing' && window.location.pathname.startsWith(REPAIR_PATH)) {
    return <>{children}</>;
  }

  if (zone.status === 'loading') {
    return (
      <div
        data-testid="tenant-zone-loading"
        role="status"
        aria-label="Loading your company's time zone"
        className="min-h-screen bg-fieldiq-dark flex items-center justify-center"
      >
        <div className="text-fieldiq-accent">Loading...</div>
      </div>
    );
  }

  const refused = zone.reason === 'refused';
  const Glyph = refused ? Lock : AlertTriangle;
  const heading =
    zone.reason === 'missing'
      ? 'Your company has no time zone set'
      : refused
        ? 'You don’t have permission to read your company’s time zone'
        : 'Couldn’t load your company’s time zone';
  const detail =
    zone.reason === 'missing'
      ? 'Every date in FieldIQ is shown in the time zone set under Settings → Regional & Tax. Ask an admin to set it.'
      : refused
        ? 'Dates cannot be shown without it. Ask an admin to check your account.'
        : 'Dates are shown in your company’s time zone, and this device has not loaded it yet. Connect to the internet and reopen the app.';

  return (
    <div className="min-h-screen bg-fieldiq-dark flex items-center justify-center px-4">
      <div
        role="status"
        data-testid="tenant-zone-error"
        data-tenant-zone-reason={zone.reason}
        className="max-w-[46ch] text-center"
      >
        <Glyph aria-hidden="true" className="mx-auto mb-3 size-10 text-fieldiq-muted" />
        <p className="text-sm text-fieldiq-text">{heading}</p>
        <p className="mt-1 text-xs text-fieldiq-muted">{detail}</p>
        <button
          type="button"
          onClick={logout}
          className="mt-6 min-h-[44px] rounded-xl border border-fieldiq-border bg-fieldiq-card px-4 text-sm text-fieldiq-text"
        >
          Sign out
        </button>
      </div>
    </div>
  );
}

export default TenantZoneGate;
